import {
  dashboardOrganizationMemberAddRequestSchema,
  dashboardOrganizationMemberUpdateRequestSchema,
} from "@arti/schemas/dashboard";
import { useCallback, useEffect, useState } from "react";

import {
  type DashboardOrganizationInvite,
  type DashboardOrganizationMember,
  type DashboardOrganizationSummary,
  getOrganizationAsUser,
  invalidateOrganizationInviteAsUser,
  inviteOrganizationMemberAsUser,
  listOrganizationInvitesAsUser,
  listOrganizationMembersAsUser,
  removeMemberAsUser,
  updateMemberRoleAsUser,
} from "../dashboard-client";

interface UseOrganizationState {
  org: DashboardOrganizationSummary | null;
  members: DashboardOrganizationMember[];
  invites: DashboardOrganizationInvite[];
  loading: boolean;
  error: string | null;
}

export function useOrganization(organizationId: string | null) {
  const [state, setState] = useState<UseOrganizationState>({
    error: null,
    invites: [],
    loading: false,
    members: [],
    org: null,
  });

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setState({
        error: null,
        invites: [],
        loading: false,
        members: [],
        org: null,
      });
      return;
    }
    setState((s) => ({ ...s, error: null, loading: true }));
    try {
      const [org, members, invites] = await Promise.all([
        getOrganizationAsUser({ path: { organizationId } }).then((r) => r.data),
        listOrganizationMembersAsUser({ path: { organizationId } }).then(
          (r) => r.data.members,
        ),
        listOrganizationInvitesAsUser({ path: { organizationId } }).then(
          (r) => r.data.invites,
        ),
      ]);
      setState({ error: null, invites, loading: false, members, org });
    } catch (error) {
      setState((s) => ({
        ...s,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load organization",
        loading: false,
      }));
    }
  }, [organizationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const inviteMember = useCallback(
    async (email: string, role: string) => {
      if (!organizationId) {
        return;
      }
      await inviteOrganizationMemberAsUser({
        body: {
          email,
          role: dashboardOrganizationMemberAddRequestSchema.shape.role.parse(
            role,
          ),
        },
        path: { organizationId },
      });
      await refresh();
    },
    [organizationId, refresh],
  );

  const invalidateInvite = useCallback(
    async (inviteId: string) => {
      if (!organizationId) {
        return;
      }
      await invalidateOrganizationInviteAsUser({
        path: { inviteId, organizationId },
      });
      await refresh();
    },
    [organizationId, refresh],
  );

  const changeMemberRole = useCallback(
    async (memberId: string, role: string) => {
      if (!organizationId) {
        return;
      }
      await updateMemberRoleAsUser({
        body: {
          role: dashboardOrganizationMemberUpdateRequestSchema.shape.role.parse(
            role,
          ),
        },
        path: { memberId, organizationId },
      });
      await refresh();
    },
    [organizationId, refresh],
  );

  const deleteMember = useCallback(
    async (memberId: string) => {
      if (!organizationId) {
        return;
      }
      await removeMemberAsUser({ path: { memberId, organizationId } });
      await refresh();
    },
    [organizationId, refresh],
  );

  return {
    changeMemberRole,
    deleteMember,
    error: state.error,
    invalidateInvite,
    inviteMember,
    invites: state.invites,
    loading: state.loading,
    members: state.members,
    org: state.org,
    refresh,
  };
}
